import { z } from 'zod';

/**
 * Mirrors the Postgres `operation_log_entry_type_enum` — kept in sync with
 * `packages/db/src/schema/index.ts`.
 */
export const operationLogEntryTypeValues = [
  'status_change',
  'alert',
  'supplier_update',
  'tour_operator_update',
  'whatsapp_notification',
  'date_update',
  'whatsapp_confirmation',
  'itinerary_status_change',
  'itinerary_budget_change',
  'itinerary_undated_change',
  'itinerary_duration_change',
  'itinerary_notes_change',
  'added_removed',
  'modified',
  'itinerary_travelers_contact_info_update',
  'notes_update',
  'itinerary_duplicate',
  'itinerary_disable',
  'itinerary_delete',
  'itinerary_change_owner',
  'itinerary_add_collaborators',
  // Bulk-reassign envelope row: written once when a status is emptied via
  // `itineraries.reassignStatus` (carries the affected itinerary ids in
  // `oldValue` for rollback). NOT reversible — rollback happens through the
  // per-itinerary `itinerary_status_change` rows or the id list in the row.
  'itinerary_status_bulk_reassign',
  'venue_update',
  // Item-level types written by the legacy app; the values already exist in
  // the shared Postgres `operation_log_entry_type_enum`, so this is a
  // declaration sync (no migration) that lets the new app label + render them
  // instead of falling back to a generic "Log entry".
  'title_update',
  'description_update',
  'price_update',
  'location_update',
  'item_travellers_update',
  'details_update',
  'accounting_ref_update',
  'item_duplicate',
  'service_type_update',
  'visibility_change',
  'optional_items_update',
  'category_update',
  'priority_change',
  'attachment_update',
  'booking_status_change',
  'task_created',
  'task_updated',
  'task_completed',
  'task_deleted',
  'task_snoozed',
] as const;

export const operationLogEntryTypeSchema = z.enum(operationLogEntryTypeValues);
export type OperationLogEntryType = z.infer<typeof operationLogEntryTypeSchema>;

/**
 * Entry types that `undo` can reverse. Anything else must be rejected —
 * e.g. `alert` and the `whatsapp_*` entries are informational.
 */
export const reversibleOperationLogEntryTypes = [
  // Item-level (only those with implemented reversal logic).
  'status_change',
  'notes_update',
  // Item/subitem add-or-remove — undo toggles the item's soft-delete and
  // writes a reverse entry, matching legacy.
  'added_removed',
  // Item duplication — undo soft-deletes the duplicated item (the entry's
  // `itineraryItemId` is the copy). Legacy parity.
  'item_duplicate',
  // Itinerary-level — restore the single field captured in `oldValue`.
  // Mirrors legacy's `undo()` switch. The pure item field-edit types
  // (date/title/venue/location/service-type) are NOT listed: the new app
  // consolidates those into a single `modified` entry, which is non-reversible
  // in both repos, so they intentionally get no Undo button here.
  'itinerary_status_change',
  'itinerary_notes_change',
  'itinerary_disable',
  'itinerary_change_owner',
  'itinerary_add_collaborators',
  'itinerary_duplicate',
  'itinerary_delete',
  // Active-version field restores (budget / accounting ref / travellers
  // contact) and the date-type pair (undated flag + duration fields).
  'itinerary_budget_change',
  'accounting_ref_update',
  'itinerary_travelers_contact_info_update',
  'itinerary_undated_change',
  'itinerary_duration_change',
] as const satisfies readonly OperationLogEntryType[];

export type ReversibleOperationLogEntryType =
  (typeof reversibleOperationLogEntryTypes)[number];

export const isReversibleOperationLogEntryType = (
  entryType: OperationLogEntryType
): entryType is ReversibleOperationLogEntryType =>
  (
    reversibleOperationLogEntryTypes as readonly OperationLogEntryType[]
  ).includes(entryType);

/**
 * The `action` recorded in an `added_removed` entry's `newValue`. One entry
 * type covers both directions, so this is the only thing that says whether the
 * item was created or deleted — the Change Logs UI reads it to label the row
 * and offers it as a filter of its own.
 */
export const operationLogLifecycleActionValues = [
  'Created',
  'Removed',
] as const;

export const operationLogLifecycleActionSchema = z.enum(
  operationLogLifecycleActionValues
);

export type OperationLogLifecycleAction = z.infer<
  typeof operationLogLifecycleActionSchema
>;

export const operationLogSchema = z.object({
  id: z.string(),
  entryType: operationLogEntryTypeSchema,
  itineraryItemId: z.string().nullable(),
  itineraryId: z.string().nullable(),
  oldValue: z.unknown(),
  newValue: z.unknown(),
  createdById: z.string().nullable(),
  // Resolved display name of the acting account (firstName + lastName, or
  // email) — `null` for system/legacy rows without a joinable account.
  createdByName: z.string().nullable(),
  // Raw i18n jsonb title of the item the row hangs off (for the
  // itinerary-scoped "Item" column); `null` for itinerary-level entries.
  // Resolved to a display string on the client.
  itemTitle: z.unknown(),
  // Parent of that item when the row belongs to a subitem — lets the "Item"
  // column read "Parent › Subitem" instead of a bare title with no hint of
  // where it hangs. Both `null` for main items and itinerary-level entries.
  itemParentId: z.string().nullable(),
  itemParentTitle: z.unknown(),
  createdOn: z.string(),
  initiator: z.string().nullable(),
});

export type OperationLog = z.infer<typeof operationLogSchema>;

export const paginatedOperationLogSchema = z.object({
  data: z.array(operationLogSchema),
  total: z.number(),
  countAll: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type PaginatedOperationLog = z.infer<typeof paginatedOperationLogSchema>;

export const findOperationLogByItineraryInput = z.object({
  itineraryId: z.uuid(),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(10),
  entryType: operationLogEntryTypeSchema.optional(),
  // Narrow the itinerary-wide history to a single item (drops every other
  // item's rows AND the itinerary-level ones).
  itineraryItemId: z.uuid().optional(),
  // With `itineraryItemId`, also keep that item's subitems — how Operations
  // opens an item's history, so Planner subitem activity shows there (XT-399).
  includeSubitems: z.boolean().optional(),
  // Narrow `added_removed` to one direction — matched against
  // `new_value->>'action'`, so it composes with `entryType`.
  lifecycleAction: operationLogLifecycleActionSchema.optional(),
});

export type FindOperationLogByItineraryInput = z.infer<
  typeof findOperationLogByItineraryInput
>;

// Options for the Change Logs "Item" filter: every item of the itinerary that
// actually has log rows, with how many. Deleted items are included — their
// history is exactly what an investigation needs.
export const findOperationLogItemsInput = z.object({
  itineraryId: z.uuid(),
});

export type FindOperationLogItemsInput = z.infer<
  typeof findOperationLogItemsInput
>;

export const operationLogItemOptionSchema = z.object({
  id: z.string(),
  title: z.unknown(),
  parentId: z.string().nullable(),
  parentTitle: z.unknown(),
  isDeleted: z.boolean(),
  // Includes the rows of the items it was copied from (T-312).
  entryCount: z.number(),
  // A newer itinerary version holds a copy of it, which carries this history.
  hasNewerCopy: z.boolean(),
});

export type OperationLogItemOption = z.infer<
  typeof operationLogItemOptionSchema
>;

// Org-wide recent-activity feed (Operations Dashboard v2 "Recent changes").
// No item/itinerary scope — returns the org's latest operation-log entries in a
// rolling time window.
export const findRecentOperationLogInput = z.object({
  limit: z.number().int().positive().max(50).default(20),
  sinceHours: z.number().int().positive().max(168).default(24),
});

export type FindRecentOperationLogInput = z.infer<
  typeof findRecentOperationLogInput
>;

export const undoOperationLogInput = z.object({
  logId: z.uuid(),
});

export type UndoOperationLogInput = z.infer<typeof undoOperationLogInput>;
